import type { SendReadinessAction } from "../../../../../core/src/review/send-readiness-types";
import type { SolFactDisagreement } from "../../../../../core/src/workflow/sol-fact-authority-types";
import type { RequestDetail } from "@/api";
import type { TFunc } from "../../i18n";
import { workspaceView } from "../../api/workspace-view";

/** Resolve subjects from typed identities, never from customer prose or task descriptions. */
export function taskTitle(data: RequestDetail, t: TFunc, id: string | undefined, kind: string | undefined, field?: string): string {
  const task = workspaceView(data).tasks.find(item => item.id === id);
  const operationFields = new Map(data.workflow?.payload?.fact_operations.map(op => [op.id, op.field_path]));
  const fields = field ? [field] : task?.disagreement ? [task.disagreement.field_path] : (task?.fact_refs ?? []).flatMap(ref => {
    const path = operationFields.get(ref) ?? /^fact:([^:]+):\d+$/.exec(ref)?.[1];
    return path ? [path] : [];
  });
  const subject = [...new Set(fields.map(path => t(`field.${path}`, undefined, path)))].join(", ");
  const action = t(`workflow.task.${kind ?? task?.reason_code}`);
  return subject ? `${subject} · ${action}` : action;
}

/** The checklist and details dialog name the same server action identically. `done`: the one-click
 * step that settles a point of an offer (#2516); the checklist offers only that step. */
export function taskActionLabel(kind: string | undefined, action: SendReadinessAction, done = false): string {
  if (kind === "fact_disagreement" && action === "reply_confirmed") return "sendReady.disagreement.confirm";
  if (kind === "outside_hamburg" && (action === "approved" || action === "refused")) return `sendReady.delivery.${action}`;
  if (done && action === "approved") return "sendReady.action.done";
  return `sendReady.action.${action}`;
}

/** Keep distinct source locations; only repeated references to the same quote collapse. */
export function distinctDisagreementEvidence(evidence: SolFactDisagreement["evidence"]) {
  const seen = new Set<string>();
  return evidence.filter(e => {
    const key = JSON.stringify([e.message_id, e.source, e.source_hash, e.start, e.text]);
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
}
